import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/services/api'
import { JWTManager } from '@/utils/jwt'

export interface User {
  user_id: string
  username: string
  role: 'admin' | 'party_admin' | 'contributor'
  created_at: string
}

export interface LoginCredentials {
  username: string
  password: string
}

export interface AuthResponse {
  token: string
  user: User
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string | null>(JWTManager.getToken())
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => {
    if (!token.value) return false
    
    // Check if token is expired
    if (JWTManager.isTokenExpired(token.value)) {
      logout()
      return false
    }
    
    return !!user.value
  })
  
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isPartyAdmin = computed(() => user.value?.role === 'party_admin')
  const isContributor = computed(() => user.value?.role === 'contributor')

  // Actions
  const login = async (credentials: LoginCredentials): Promise<void> => {
    loading.value = true
    error.value = null
    
    try {
      const response = await apiClient.post<AuthResponse>('/auth', credentials)
      const { token: authToken, user: userData } = response.data
      
      token.value = authToken
      user.value = userData
      
      // Store token using JWT manager
      JWTManager.setToken(authToken)
      
      // Set default authorization header for future requests
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${authToken}`
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Login failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = (): void => {
    token.value = null
    user.value = null
    error.value = null
    
    // Remove token using JWT manager
    JWTManager.removeToken()
    
    // Remove authorization header
    delete apiClient.defaults.headers.common['Authorization']
  }

  const initializeAuth = (): void => {
    const storedToken = JWTManager.getToken()
    if (storedToken && !JWTManager.isTokenExpired(storedToken)) {
      token.value = storedToken
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`
      
      // Try to get user info from token
      const userFromToken = JWTManager.getUserFromToken()
      if (userFromToken) {
        user.value = {
          user_id: userFromToken.user_id,
          username: userFromToken.username,
          role: userFromToken.role as 'admin' | 'party_admin' | 'contributor',
          created_at: '' // This would need to come from the server
        }
      }
    } else {
      // Token is expired or doesn't exist, clear everything
      logout()
    }
  }

  const refreshUserData = async (): Promise<void> => {
    if (!isAuthenticated.value) return
    
    try {
      // In a real app, you might have an endpoint to get current user data
      // For now, we'll rely on the token data
      const userFromToken = JWTManager.getUserFromToken()
      if (userFromToken) {
        user.value = {
          user_id: userFromToken.user_id,
          username: userFromToken.username,
          role: userFromToken.role as 'admin' | 'party_admin' | 'contributor',
          created_at: user.value?.created_at || ''
        }
      }
    } catch (err) {
      console.error('Failed to refresh user data:', err)
      logout()
    }
  }

  const clearError = (): void => {
    error.value = null
  }

  return {
    // State
    token,
    user,
    loading,
    error,
    // Getters
    isAuthenticated,
    isAdmin,
    isPartyAdmin,
    isContributor,
    // Actions
    login,
    logout,
    initializeAuth,
    refreshUserData,
    clearError
  }
})